/**
 * 支付失败 · 购物车「切换支付方式」回 Home 恢复
 *
 * 流程：
 * 1. Result 点击 tip「切换支付方式」→ 跳转 Home
 *    URL：payResume=cartSwitch & payCartOrderId={原订单号}
 * 2. Home(SelectProduct) 检测到 URL → get_cart(原订单号) 拉明细
 * 3. 按 matchCpProductid 匹配当前 product_list 并加购
 *    - true|null：同时匹配 productId + cpProductId
 *    - false：只匹配 productId
 *    - 匹配不到：跳过该商品，不中断其他加购
 * 4. 有成功加购 → 拉起收银台；再提示被跳过的商品
 */

import globalConfig from '@utiles/officialStore/globalConfig';
import {
  GET_CART,
  CLEAR_CART,
  ADD_PRODUCT_TO_CART,
  SET_PRODUCT_OF_CART,
} from '@store/officialTopupNew/cart';
import {
  PAY_FAIL_RESUME_CART_SWITCH,
  consumePayFailSwitchPaytypeResume,
} from './payFailResume';
import { getEntryTexts } from './instance';

/**
 * 归一化 get_cart 明细字段（兼容 camelCase / 大写）
 * @param {Record<string, unknown>} item
 */
function normalizeCartOrderItem(item = {}) {
  return {
    productId: item.productId || item.PRODUCT_ID || '',
    cpProductId: item.cpProductId || item.CP_PRODUCT_ID || '',
    quantity: Number(item.quantity) || 1,
    remark: item.remark || item.REMARK || '',
    productDesc: item.productDesc || item.PRODUCT_DESC || item.CP_PRODUCT_DESC || '',
  };
}

/**
 * 按页面配置 matchCpProductid 匹配商品（不看 amount）
 * - true | null：必须同时匹配 productId + cpProductId
 * - false：只匹配 productId
 * @param {Array<Record<string, unknown>>} productList
 * @param {ReturnType<typeof normalizeCartOrderItem>} cartItem
 * @param {boolean|null} [matchCpProductid]
 */
function findProductForCartItem(productList, cartItem, matchCpProductid) {
  const { productId, cpProductId } = cartItem;
  if (!productId || !Array.isArray(productList)) return null;

  // false：仅 productId
  if (matchCpProductid === false) {
    return (
      productList.find(
        (product) =>
          product && String(product.PRODUCT_ID) === String(productId),
      ) || null
    );
  }

  // true | null：双字段匹配；缺 cpProductId 无法匹配
  if (!cpProductId) return null;
  return (
    productList.find(
      (product) =>
        product &&
        String(product.PRODUCT_ID) === String(productId) &&
        String(product.CP_PRODUCT_ID) === String(cpProductId),
    ) || null
  );
}

/**
 * 拆成可加购 / 跳过两组；跳过不抛错，保证其余商品可继续恢复
 * @param {Array<Record<string, unknown>>} cartOrderList
 * @param {Array<Record<string, unknown>>} productList
 * @param {boolean|null} [matchCpProductid]
 * @returns {{
 *   resolved: Array<{ cartProduct: Record<string, unknown>, quantity: number }>,
 *   skipped: Array<ReturnType<typeof normalizeCartOrderItem>>,
 * }}
 */
function resolveCartProducts(cartOrderList, productList, matchCpProductid) {
  const resolved = [];
  const skipped = [];

  cartOrderList.forEach((raw) => {
    const cartItem = normalizeCartOrderItem(raw);
    const product = findProductForCartItem(
      productList,
      cartItem,
      matchCpProductid,
    );
    // 列表无此商品或无有效定价 → 跳过（下架/售罄等）
    if (!product || !product.amountLevel || !product.amountLevel.TRANS_AMOUNT) {
      skipped.push(cartItem);
      return;
    }
    const cartProduct = { ...product };
    if (cartItem.remark) {
      cartProduct.REMARK = cartItem.remark;
    }
    resolved.push({
      cartProduct,
      quantity: cartItem.quantity,
    });
  });

  return { resolved, skipped };
}

/** 是否购物车「切换支付方式」回跳入口 */
export function isPayFailCartSwitchEntry() {
  return globalConfig.getHrefParam('payResume') === PAY_FAIL_RESUME_CART_SWITCH;
}

/** 取回跳 URL 上的原订单号（get_cart 用原单号，不是 get_orders 的 orderId+「1」） */
export function getPayFailCartSwitchOrderId() {
  if (!isPayFailCartSwitchEntry()) return '';
  return globalConfig.getHrefParam('payCartOrderId') || '';
}

/** 恢复流程结束后清理 URL，避免刷新重复加购 */
export function consumePayFailCartSwitchResume() {
  consumePayFailSwitchPaytypeResume();
}

/**
 * 跳过商品的提示文案（在拉起收银台后再展示）
 * 文案来自 entryTexts.payFailCartSkippedTip；无配置则不展示
 * @param {Array<ReturnType<typeof normalizeCartOrderItem>>} skipped
 * @returns {string}
 */
export function buildSkippedCartProductsTip(skipped = []) {
  if (!skipped.length) return '';
  return getEntryTexts().payFailCartSkippedTip || '';
}

/**
 * 按失败订单号 get_cart → 匹配商品 → 写入 vuex cartList
 * - 匹配不到的商品跳过，不中断其他加购
 * - 全部匹配完成后再 CLEAR + 写入，避免中途失败留下半截购物车
 * @param {import('vuex').Store} store
 * @param {{
 *   orderId: string|number,
 *   productList: Array<Record<string, unknown>>,
 *   matchCpProductid?: boolean|null,
 * }} options
 * @returns {Promise<{ addedCount: number, skipped: Array<ReturnType<typeof normalizeCartOrderItem>> }>}
 */
export async function restoreCartFromFailedOrder(store, options = {}) {
  const { orderId, productList, matchCpProductid = null } = options;
  if (!orderId) {
    throw new Error('missing orderId');
  }

  // 用原订单号查购物车明细（与 Result 页 lookOrderRecord 同源接口）
  const rawList = await store.dispatch(GET_CART, orderId);
  const cartOrderList = Array.isArray(rawList) ? rawList : [];
  if (!cartOrderList.length) {
    throw new Error('empty cart order');
  }

  const { resolved, skipped } = resolveCartProducts(
    cartOrderList,
    productList,
    matchCpProductid,
  );

  store.commit(CLEAR_CART);
  resolved.forEach(({ cartProduct, quantity }) => {
    store.commit(ADD_PRODUCT_TO_CART, cartProduct);
    // ADD 默认 +1；数量 >1 时再按 limit 设为最终数量
    if (quantity > 1) {
      store.commit(SET_PRODUCT_OF_CART, {
        ...cartProduct,
        limit: quantity,
      });
    }
  });

  return {
    addedCount: resolved.length,
    skipped,
  };
}
